import { isTag } from 'anodum';
import getAdvancedSelection from '../../text-formatting/accessors/get-advanced-selection';
import findRootEditableElement from './find-root-editable-element';
import canBeRichEdited from '../../constraints/verifiers/can-be-rich-edited';
import unwrapElement from './unwrap-element';
import setCaretAtTheBeginningOfNode from './set-caret-at-the-beginning-of-node';
import wrappersTypes from '../../text-formatting/constants/wrappers-types';
import {
  DEFAULT_BLOCK_TAG,
  LIST_ELEMENTS,
  LIST_ITEM,
  BREAK_TAG,
} from '../constants/editable-elements';
import normalizeStyleAttribute from './normalize-style-attribute';

const { customColor, decoration } = wrappersTypes;

const handleNativeInputBehavior = (e) => {
  const closestEditableElement = e.srcElement.closest('[contenteditable]');

  if (!closestEditableElement) return;

  if (!canBeRichEdited(closestEditableElement)) {
    /*
     * On past text to <p/> element, execution of command `execCommand('insertText')`
     * inserts not semantic structure with <div/>. After reload editor it makes bug with
     * not contenteditable text inside globals.
     * https://github.com/yola/production/issues/10525
     */
    if (isTag(closestEditableElement, DEFAULT_BLOCK_TAG)) {
      const { ownerDocument: liveDocument } = closestEditableElement;

      const cleanupDivElements = (element) => {
        [...element.children].forEach((child) => {
          if (isTag(child, 'div')) {
            cleanupDivElements(child);
          }
        });

        const isContentEditable = element.hasAttribute('contenteditable');

        if (isContentEditable) return;

        const fragment = liveDocument.createDocumentFragment();

        if ([...element.children].find((child) => isTag(child, BREAK_TAG))) {
          fragment.append(...element.childNodes);
        } else {
          const breakElement = liveDocument.createElement(BREAK_TAG);

          fragment.append(breakElement, ...element.childNodes);
        }

        element.parentNode.insertBefore(fragment, element);
        element.remove();
      };

      cleanupDivElements(closestEditableElement);
    }

    return;
  }

  const { anchorNode } = getAdvancedSelection();
  requestAnimationFrame(() => {
    const editableElement = findRootEditableElement(anchorNode);

    const { ownerDocument: liveDocument } = closestEditableElement;

    /*
     * If list comes without correct structure we need to add child and make focus to
     * simulate normal behaviour of list deleting, for example Safari browser after deleting list it works incorrect,
     * removes all children inside list but shouldn't, so this fix will help to avoid it
     * https://github.com/yola/production/issues/10661
     */
    closestEditableElement.querySelectorAll(LIST_ELEMENTS.join()).forEach((list) => {
      if (
        !list.querySelector(LIST_ITEM) ||
        (list.querySelectorAll(LIST_ITEM).length === 1 &&
          !list.querySelector(`${LIST_ITEM} > *:not(br)`))
      ) {
        // eslint-disable-next-line no-param-reassign
        list.innerHTML = '';

        const listItem = liveDocument.createElement(LIST_ITEM);
        const paragraph = liveDocument.createElement(DEFAULT_BLOCK_TAG);
        const breakElement = liveDocument.createElement(BREAK_TAG);

        paragraph.appendChild(breakElement);
        listItem.appendChild(paragraph);
        list.appendChild(listItem);

        setCaretAtTheBeginningOfNode(breakElement);
      }
    });

    if (!editableElement) return;

    // It's necessary for handling unsupported elements which appears while editing custom color
    editableElement
      .querySelectorAll(`${customColor.tagName} font`)
      .forEach((el) => unwrapElement(el));

    // during the backspace event, the browser transfers styles from the parent block to the child block
    // and wraps them in the <span> (this happens if the styles have custom variables)
    // https://stackoverflow.com/questions/19243432/prevent-contenteditable-mode-from-creating-span-tags
    // https://bugs.chromium.org/p/chromium/issues/detail?id=226941
    // so we clean up the unnecessary <span>
    editableElement.querySelectorAll('span[style]').forEach((el) => {
      const fragment = liveDocument.createDocumentFragment();
      fragment.append(...el.childNodes);
      el.parentNode.replaceChild(fragment, el);
    });
    editableElement
      .querySelectorAll(`[style]:not(${customColor.tagName}):not(${decoration.tagName})`)
      .forEach((el) => {
        el.removeAttribute('style');
      });
    editableElement.querySelectorAll(customColor.tagName).forEach((color) => {
      normalizeStyleAttribute(color);
    });
    editableElement.normalize();

    // when user select existing text wrapped in ws-color and apply native color editing using touchbar
    // Safari don't wrap it into <font> again but instead remove style attribute from our ws-color tag
    // as there is no color applied anyway we need to unwrap such nodes to keep markup clean
    const wsColorsWithoutStyles = [
      ...editableElement.querySelectorAll(wrappersTypes.customColor.tagName),
    ].filter((element) => !element.hasAttribute('style'));

    wsColorsWithoutStyles.forEach((element) => {
      unwrapElement(element);
    });
  });
};

export default handleNativeInputBehavior;
